
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { useNotifications } from "@/hooks/use-notifications";
import { Skeleton } from "@/components/ui/skeleton";
import { checkDeadlines } from "@/utils/notificationUtils";

const NotificationDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { 
    notifications, 
    unreadCount, 
    isLoading, 
    markAsRead,
    markAllAsRead,
    refresh
  } = useNotifications();
  const navigate = useNavigate();

  // Check for deadline notifications when dropdown opens
  useEffect(() => {
    if (isOpen) {
      checkDeadlines().then(() => {
        refresh();
      });
    }
  }, [isOpen, refresh]);

  const handleNotificationClick = (notification: any) => {
    // Mark as read first
    markAsRead(notification.id);
    
    // Navigate based on notification type
    if (notification.type.includes('mfo')) {
      navigate('/dashboard/mfo');
    } else if (notification.type.includes('task') || notification.type.includes('kanban') || notification.type.includes('deadline')) {
      navigate('/dashboard/kanban');
    } else if (notification.type.includes('employee')) {
      navigate('/dashboard/employees');
    } else if (notification.type.includes('department')) {
      navigate('/dashboard/divisions');
    }
    
    setIsOpen(false);
  };

  const viewAllNotifications = () => {
    navigate('/dashboard/notifications');
    setIsOpen(false);
  };

  const getNotificationIcon = (type: string) => {
    if (type.includes('deadline')) {
      return (
        <span className="flex h-2 w-2 rounded-full bg-destructive mr-2"></span>
      );
    }
    return !type.includes('read') ? (
      <span className="flex h-2 w-2 rounded-full bg-primary mr-2"></span>
    ) : null;
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 px-1 min-w-[18px] h-[18px] text-[10px] flex items-center justify-center"
            >
              {unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex justify-between items-center">
          <span>Notifications</span>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-7 px-2"
              onClick={() => markAllAsRead()}
            >
              Mark all as read
            </Button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup className="max-h-[300px] overflow-y-auto">
          {isLoading ? (
            <div className="py-2 space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="px-4 space-y-1">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="h-3 w-1/4" />
                </div>
              ))}
            </div>
          ) : notifications.length === 0 ? (
            <div className="py-4 text-center text-muted-foreground">
              No notifications
            </div>
          ) : (
            notifications.slice(0, 5).map((notification) => (
              <DropdownMenuItem
                key={notification.id}
                className={`flex flex-col items-start py-2 px-4 cursor-pointer ${
                  !notification.is_read ? "bg-muted/40" : ""
                } ${notification.type.includes('deadline') ? "border-l-2 border-destructive" : ""}`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="flex justify-between w-full">
                  <span className="font-medium flex items-center">
                    {getNotificationIcon(notification.type)}
                    {notification.title}
                  </span>
                  {!notification.is_read && !notification.type.includes('deadline') && (
                    <span className="h-2 w-2 rounded-full bg-primary"></span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2 pl-4">
                  {notification.message}
                </p>
                <span className="text-xs text-muted-foreground mt-1 pl-4">
                  {formatDistanceToNow(new Date(notification.created_at), {
                    addSuffix: true,
                  })}
                </span>
              </DropdownMenuItem>
            ))
          )}
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="w-full justify-center font-medium"
          onClick={viewAllNotifications}
        >
          View all notifications
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationDropdown;
